import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CleanContainer, createCleanMarkdownComponents, normalizeMarkdownBulletLists } from '../utils/markdownHelpers';
import { processQuotes } from '../utils/general';
import CodeBlockWithEmail from './CodeBlockWithEmail';
import SourcesPills from '../../SourcesPills';

// Component to handle streaming text with fade-in animations for new chunks
const StreamingMarkdown = ({ children, isStreaming, darkMode, toolIndicators, onSendDraft, sendDraftLoading = false, sentDrafts = [], chatTitle, onOpenArtifact, messageId, chatId, onDraftSent, onSaveDraftContent, currentMessageText, responseIndex, ...props }) => {
  const markdownComponents = useMemo(() => 
    createCleanMarkdownComponents(darkMode, 0, CodeBlockWithEmail, { 
      onSend: onSendDraft, 
      sendDraftLoading, 
      sentDrafts, 
      chatTitle, 
      isStreaming,
      onOpenArtifact,
      messageId,
      chatId,
      onDraftSent,
      onSaveDraftContent,
      currentMessageText,
      responseIndex,
    }), 
    [darkMode, onSendDraft, sendDraftLoading, sentDrafts, chatTitle, isStreaming, onOpenArtifact, messageId, chatId, onDraftSent, onSaveDraftContent, currentMessageText, responseIndex]
  );

  // Handle case where text is empty or undefined
  if (!children) {
    return toolIndicators ? (
      <div className="mb-2">
        {toolIndicators}
      </div>
    ) : null;
  }

  // Extract sources from markdown and separate main content
  const extractSourcesFromMarkdown = (text) => {
    const sourcesRegex = /\n\n\*\*Sources:\*\*\n((?:(?:\d+\.\s*\[.+?\]\(.+?\)\n?)+))/;
    const match = text.match(sourcesRegex);
    
    if (match) {
      const sourcesText = match[1];
      const mainContent = text.replace(match[0], '');
      
      // Parse individual source links from the sources section
      const sourceMatches = sourcesText.match(/\d+\.\s*\[(.+?)\]\((.+?)\)/g);
      const allSources = sourceMatches ? sourceMatches.map(sourceMatch => {
        const linkMatch = sourceMatch.match(/\[(.+?)\]\((.+?)\)/);
        return linkMatch ? {
          title: linkMatch[1],
          url: linkMatch[2]
        } : null;
      }).filter(Boolean) : [];
      
      // Normalize URL for better deduplication (handles www, trailing slashes, etc.)
      const normalizeUrl = (url) => {
        try {
          const urlObj = new URL(url);
          // Remove www. prefix and trailing slash for comparison
          const normalizedHost = urlObj.hostname.replace(/^www\./, '');
          const normalizedPath = urlObj.pathname.replace(/\/$/, '') || '/';
          return `${normalizedHost}${normalizedPath}${urlObj.search}`;
        } catch (e) {
          // If URL parsing fails, return original for exact matching
          return url.replace(/^www\./, '').replace(/\/$/, '');
        }
      };

      // Deduplicate sources by normalized URL - only show each unique domain/path once
      const uniqueSources = allSources.reduce((acc, source) => {
        const normalizedUrl = normalizeUrl(source.url);
        const existingSource = acc.find(s => normalizeUrl(s.url) === normalizedUrl);
        if (!existingSource) {
          acc.push(source);
        }
        return acc;
      }, []);
      
      return { mainContent, sources: uniqueSources };
    }
    
    return { mainContent: text, sources: [] };
  };

  // Process the text content
  const processedText = normalizeMarkdownBulletLists(processQuotes(children));
  const { mainContent, sources } = extractSourcesFromMarkdown(processedText);
  
  // Split text into parts to insert tool indicators at appropriate positions
  const renderContent = () => {
    // If we have tool indicators, render them before the text content
    if (toolIndicators) {
      return (
        <>
          <div className="mb-2">
            {toolIndicators}
          </div>
          <CleanContainer>
            <ReactMarkdown
              components={markdownComponents}
              remarkPlugins={[remarkGfm]}
              {...props}
            >
              {mainContent}
            </ReactMarkdown>
          </CleanContainer>
          {sources.length > 0 && <SourcesPills sources={sources} />}
        </>
      );
    }
    
    // No tool indicators, render normally
    return (
      <>
        <CleanContainer>
          <ReactMarkdown
            components={markdownComponents}
            remarkPlugins={[remarkGfm]}
            {...props}
          >
            {mainContent}
          </ReactMarkdown>
        </CleanContainer>
        {sources.length > 0 && <SourcesPills sources={sources} />}
      </>
    );
  };

  return (
    <div className="streaming-markdown">
      {renderContent()}
    </div>
  );
};

export default StreamingMarkdown; 